import { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Tabs,
    Tab,
    TextField,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { usePlans, useArchivePlan, useDeletePlan, useDownloadPlan } from '../../hooks/usePlans';
import { useSnackbar } from 'notistack';
import PlansTable from './PlansTable';
import PlansEmptyState from './PlansEmptyState';
import CreatePlanModal from './CreatePlanModal';
import DashboardHeaderSection from '../common/dashboardheader/DashboardHeader';

const SORT_OPTIONS = [
    { value: 'recently_opened', label: 'Recently opened' },
    { value: 'recently_created', label: 'Recently created' },
    { value: 'plan_name', label: 'Plan name' },
];

const OWNER_OPTIONS = [
    { value: 'anyone', label: 'Anyone' },
    { value: 'me', label: 'Me' },
];

const sortPlans = (plans, sortBy) => {
    const copy = [...plans];
    switch (sortBy) {
        case 'plan_name':
            return copy.sort((a, b) => a.name.localeCompare(b.name));
        case 'recently_created':
            return copy.sort(
                (a, b) => new Date(b.createdOn) - new Date(a.createdOn)
            );
        case 'recently_opened':
        default:
            return copy.sort(
                (a, b) =>
                    new Date(b.lastOpenedOn ?? b.createdOn) -
                    new Date(a.lastOpenedOn ?? a.createdOn)
            );
    }
};

const Plans = () => {
    const [tab, setTab] = useState('active');
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('recently_opened');
    const [owner, setOwner] = useState('anyone');
    const [sortAnchorEl, setSortAnchorEl] = useState(null);
    const [ownerAnchorEl, setOwnerAnchorEl] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [downloadingPlanId, setDownloadingPlanId] = useState(null);

    const { enqueueSnackbar } = useSnackbar();

    // Fetch plans data
    const { data, isLoading, isPlaceholderData, isError, error, refetch } = usePlans(tab);
    const plans = useMemo(() => data?.plans ?? [], [data]);
    // usePlans seeds placeholderData ({ plans: [] }) so switching tabs doesn't
    // flash a blank table — but that placeholder flips isLoading to false
    // immediately (React Query treats it as a "success" render), so isLoading
    // alone can't tell a real empty result apart from the fetch still being in
    // flight. isPlaceholderData is what actually distinguishes them.
    const stillLoading = isLoading || isPlaceholderData;

    // Use counts from API response
    const activeCount = data?.activeCount ?? 0;
    const archivedCount = data?.archivedCount ?? 0;

    // Mutations
    const archiveMutation = useArchivePlan();
    const deleteMutation = useDeletePlan();
    const downloadMutation = useDownloadPlan();

    const visiblePlans = useMemo(() => {
        let result = plans;
        if (owner === 'me') {
            result = result.filter((p) => p.isMine);
        }
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            result = result.filter(
                (p) =>
                    p.name.toLowerCase().includes(q) ||
                    p.brand?.toLowerCase().includes(q)
            );
        }
        return sortPlans(result, sortBy);
    }, [plans, owner, search, sortBy]);

    const currentSortLabel = SORT_OPTIONS.find(
        (o) => o.value === sortBy
    )?.label;

    const currentOwnerLabel = OWNER_OPTIONS.find(
        (o) => o.value === owner
    )?.label;

    // Handle archive/unarchive
    const handleArchiveToggle = (plan) => {
        const isArchived = tab === 'archived';
        const action = isArchived ? 'unarchive' : 'archive';

        archiveMutation.mutate(
            {
                planId: plan.id,
                action: action,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar(
                        `Plan ${action === 'archive' ? 'archived' : 'unarchived'} successfully`,
                        { variant: 'success' }
                    );
                    refetch();
                },
                onError: (error) => {
                    enqueueSnackbar(
                        error?.response?.data?.message ||
                            `Failed to ${action} plan`,
                        { variant: 'error' }
                    );
                },
            }
        );
    };

    // Handle delete
    const handleDelete = (plan) => {
        setDeleteTarget(plan);
    };

    // Handle download — generates the .xlsx server-side and triggers the browser save
    const handleDownload = (plan) => {
        setDownloadingPlanId(plan.id);
        downloadMutation.mutate(
            { planId: plan.id },
            {
                onError: (error) => {
                    enqueueSnackbar(
                        error?.response?.data?.message ||
                            'Failed to download plan',
                        { variant: 'error' }
                    );
                },
                onSettled: () => setDownloadingPlanId(null),
            }
        );
    };

    const confirmDelete = () => {
        const plan = deleteTarget;
        setDeleteTarget(null);
        deleteMutation.mutate(
            { planId: plan.id },
            {
                onSuccess: () => {
                    enqueueSnackbar('Plan deleted successfully', {
                        variant: 'success',
                    });
                    refetch();
                },
                onError: (error) => {
                    enqueueSnackbar(
                        error?.response?.data?.message ||
                            'Failed to delete plan',
                        { variant: 'error' }
                    );
                },
            }
        );
    };

    // Loading state
    if (stillLoading && !plans.length) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <DashboardHeaderSection hideSearch />
            <Box
                sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    marginLeft: '65px',
                    marginTop: 8.2,
                    background: `linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)`,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Plans &amp; Lists
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)}
                        sx={{
                            background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                            boxShadow: '0 4px 14px rgba(255,82,0,0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                                opacity: 0.9,
                                boxShadow: '0 6px 20px rgba(255,82,0,0.45)',
                            },
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '10px',
                            px: 2.5,
                        }}
                    >
                        New Plan
                    </Button>
                </Box>

                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                        mb: 3,
                        minHeight: 0,
                        display: 'inline-flex',
                        bgcolor: '#F3F4F6',
                        borderRadius: '12px',
                        p: 0.5,
                        '& .MuiTabs-indicator': { display: 'none' },
                        '& .MuiTabs-flexContainer': { gap: 0.5 },
                    }}
                >
                    <Tab
                        value="active"
                        label={`ACTIVE (${activeCount})`}
                        disableRipple
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: 13,
                            borderRadius: '9px',
                            minHeight: 36,
                            px: 2.5,
                            transition: 'all 0.2s ease',
                            color: tab === 'active' ? '#fff' : '#6B7280',
                            background:
                                tab === 'active'
                                    ? 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)'
                                    : 'transparent',
                            boxShadow:
                                tab === 'active' ? '0 2px 8px rgba(255,82,0,0.3)' : 'none',
                            '&.Mui-selected': { color: '#fff' },
                        }}
                    />
                    <Tab
                        value="archived"
                        label={`ARCHIVED (${archivedCount})`}
                        disableRipple
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: 13,
                            borderRadius: '9px',
                            minHeight: 36,
                            px: 2.5,
                            transition: 'all 0.2s ease',
                            color: tab === 'archived' ? '#fff' : '#6B7280',
                            background:
                                tab === 'archived'
                                    ? 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)'
                                    : 'transparent',
                            boxShadow:
                                tab === 'archived' ? '0 2px 8px rgba(255,82,0,0.3)' : 'none',
                            '&.Mui-selected': { color: '#fff' },
                        }}
                    />
                </Tabs>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Typography color="text.secondary">
                        {visiblePlans.length} plan
                        {visiblePlans.length === 1 ? '' : 's'}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <TextField
                            size="small"
                            placeholder="Search plans"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 17, color: '#9CA3AF' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: { xs: '100%', sm: 200, md: 240 },
                                '& .MuiOutlinedInput-root': {
                                    background: '#F9FAFB',
                                    borderRadius: '10px',
                                    fontSize: 13.5,
                                    '& fieldset': { borderColor: '#E5E7EB' },
                                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                                    '&.Mui-focused fieldset': { borderColor: '#FF5200', borderWidth: '1.5px' },
                                },
                            }}
                        />

                        <Button
                            onClick={(e) => setOwnerAnchorEl(e.currentTarget)}
                            endIcon={<KeyboardArrowDownIcon fontSize="small" />}
                            sx={{
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                color: '#374151',
                                bgcolor: '#fff',
                                border: '1px solid #E5E7EB',
                                borderRadius: '10px',
                                px: 1.75,
                                py: 0.75,
                                fontSize: 13.5,
                                '&:hover': { bgcolor: '#F9FAFB', borderColor: '#D1D5DB' },
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{ display: { xs: 'none', sm: 'inline' }, mr: 0.5, color: '#9CA3AF', fontSize: 13.5 }}
                            >
                                Owner :
                            </Typography>
                            <Typography component="span" sx={{ fontWeight: 700, fontSize: 13.5 }}>
                                {currentOwnerLabel}
                            </Typography>
                        </Button>
                        <Menu
                            anchorEl={ownerAnchorEl}
                            open={Boolean(ownerAnchorEl)}
                            onClose={() => setOwnerAnchorEl(null)}
                            PaperProps={{ sx: { borderRadius: '12px', mt: 0.5 } }}
                        >
                            {OWNER_OPTIONS.map((opt) => (
                                <MenuItem
                                    key={opt.value}
                                    selected={opt.value === owner}
                                    onClick={() => {
                                        setOwner(opt.value);
                                        setOwnerAnchorEl(null);
                                    }}
                                >
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Menu>

                        <Button
                            onClick={(e) => setSortAnchorEl(e.currentTarget)}
                            endIcon={<KeyboardArrowDownIcon fontSize="small" />}
                            sx={{
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                color: '#374151',
                                bgcolor: '#fff',
                                border: '1px solid #E5E7EB',
                                borderRadius: '10px',
                                px: 1.75,
                                py: 0.75,
                                fontSize: 13.5,
                                '&:hover': { bgcolor: '#F9FAFB', borderColor: '#D1D5DB' },
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{ display: { xs: 'none', sm: 'inline' }, mr: 0.5, color: '#9CA3AF', fontSize: 13.5 }}
                            >
                                Sort by :
                            </Typography>
                            <Typography component="span" sx={{ fontWeight: 700, fontSize: 13.5 }}>
                                {currentSortLabel}
                            </Typography>
                        </Button>
                        <Menu
                            anchorEl={sortAnchorEl}
                            open={Boolean(sortAnchorEl)}
                            onClose={() => setSortAnchorEl(null)}
                            PaperProps={{ sx: { borderRadius: '12px', mt: 0.5 } }}
                        >
                            {SORT_OPTIONS.map((opt) => (
                                <MenuItem
                                    key={opt.value}
                                    selected={opt.value === sortBy}
                                    onClick={() => {
                                        setSortBy(opt.value);
                                        setSortAnchorEl(null);
                                    }}
                                >
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '16px',
                        border: '1px solid #E5E7EB',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                        overflow: 'hidden',
                    }}
                >
                    {isError ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                                Couldn't load plans
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 2 }}>
                                {error?.response?.data?.message ||
                                    'Something went wrong on our end.'}
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={() => refetch()}
                                sx={{ textTransform: 'none', borderRadius: '10px' }}
                            >
                                Try again
                            </Button>
                        </Box>
                    ) : stillLoading && plans.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 8,
                            }}
                        >
                            <CircularProgress sx={{ color: '#FF5200' }} />
                        </Box>
                    ) : visiblePlans.length === 0 ? (
                        <PlansEmptyState
                            isSearch={Boolean(search.trim())}
                            onNewPlan={() => setCreateOpen(true)}
                        />
                    ) : (
                        <PlansTable
                            plans={visiblePlans}
                            isArchived={tab === 'archived'}
                            onArchiveToggle={handleArchiveToggle}
                            onDelete={handleDelete}
                            onDownload={handleDownload}
                            downloadingPlanId={downloadingPlanId}
                            isLoading={
                                archiveMutation.isPending ||
                                deleteMutation.isPending
                            }
                        />
                    )}
                </Paper>

                <CreatePlanModal
                    open={createOpen}
                    onClose={() => setCreateOpen(false)}
                    existingNames={plans.map((p) => p.name)}
                    onCreated={() => {
                        refetch();
                    }}
                />

                <Dialog
                    open={Boolean(deleteTarget)}
                    onClose={() => setDeleteTarget(null)}
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <DialogTitle sx={{ fontWeight: 700 }}>Delete plan?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete "{deleteTarget?.name}"?
                            This can't be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button
                            onClick={() => setDeleteTarget(null)}
                            sx={{ textTransform: 'none', borderRadius: '10px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            color="error"
                            variant="contained"
                            sx={{ textTransform: 'none', borderRadius: '10px', boxShadow: 'none' }}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
};

export default Plans;
